import { useCallback, useEffect, useRef, useState } from "react"
import { garageApi } from "../api/endpoints"
import { ApiError } from "../api/client"
import { useI18n } from "./useI18n"
import type { Garage } from "../types/api"
import { applyRussianPlateInputMask, isValidRussianPlate, normalizePlateInput } from "../utils/plate"
import { normalizePromoInput, PROMO_MIN_LENGTH } from "../utils/promo"
import { completeStarsInvoice } from "../utils/starsPayment"

export function useGarageActivation(initialPlate = "", initialPromo = "", affiliateLink = "") {
  const { t } = useI18n()
  const [plate, setPlateState] = useState(initialPlate)
  const [promoCode, setPromoState] = useState(normalizePromoInput(initialPromo))
  const [plateError, setPlateError] = useState("")
  const [error, setError] = useState("")
  const [promoValid, setPromoValid] = useState(false)
  const [promoError, setPromoError] = useState("")
  const [bonusYears, setBonusYears] = useState(0)
  const [promoAmountStars, setPromoAmountStars] = useState<number | null>(null)
  const [promoDurationLabel, setPromoDurationLabel] = useState<string | null>(null)
  const [checked, setCheckedState] = useState(false)
  const [busy, setBusy] = useState(false)
  const promoValidationRevisionRef = useRef(0)
  const plateValidationRevisionRef = useRef(0)
  const latestAffiliateLinkRef = useRef(affiliateLink)
  const validatedPromoSnapshotRef = useRef<{ plate: string; promo: string; affiliate: string } | null>(null)
  latestAffiliateLinkRef.current = affiliateLink

  const clearPromoOffer = () => {
    setPromoAmountStars(null)
    setPromoDurationLabel(null)
  }

  const setChecked = (value: boolean) => {
    if (value) {
      const normalizedPlate = normalizePlateInput(plate)
      if (!normalizedPlate || !isValidRussianPlate(normalizedPlate) || plateError || promoError) {
        setCheckedState(false)
        return
      }
      if (promoCode.length > 0 && promoCode.length < PROMO_MIN_LENGTH) {
        setPromoError(t("garageActivation.promoInvalid"))
        setCheckedState(false)
        return
      }
    }
    setCheckedState(value)
  }

  const setPlate = (value: string) => {
    plateValidationRevisionRef.current += 1
    promoValidationRevisionRef.current += 1
    validatedPromoSnapshotRef.current = null
    setPlateState(applyRussianPlateInputMask(value))
    setPlateError("")
    setPromoValid(false)
    setBonusYears(0)
    clearPromoOffer()
    setChecked(false)
  }

  const setPromoCode = (value: string) => {
    const nextPromo = normalizePromoInput(value)
    promoValidationRevisionRef.current += 1
    validatedPromoSnapshotRef.current = null
    setPromoState(nextPromo)
    setPromoValid(false)
    setBonusYears(0)
    clearPromoOffer()
    setChecked(false)
    setError("")
    if (!nextPromo) setPromoError("")
  }

  useEffect(() => {
    promoValidationRevisionRef.current += 1
    validatedPromoSnapshotRef.current = null
    setPromoValid(false)
    setBonusYears(0)
    clearPromoOffer()
  }, [affiliateLink])

  const validatePlate = useCallback(() => {
    const normalized = normalizePlateInput(plate)
    if (!normalized || !isValidRussianPlate(normalized)) {
      setPlateError("")
      setChecked(false)
      return ""
    }
    setPlateState(normalized)
    return normalized
  }, [plate])

  const validatePlateAvailability = useCallback(async () => {
    const normalized = normalizePlateInput(plate)
    const revision = ++plateValidationRevisionRef.current
    if (!normalized || !isValidRussianPlate(normalized)) return
    try {
      const result = await garageApi.checkActivationPlate(normalized)
      if (revision !== plateValidationRevisionRef.current) return
      if (result.can_activate) {
        setPlateError("")
      } else {
        setPlateError(result.reason || t("garageActivation.plateOccupied"))
        setChecked(false)
      }
    } catch {
      if (revision !== plateValidationRevisionRef.current) return
    }
  }, [plate, t])

  useEffect(() => {
    const normalized = normalizePlateInput(plate)
    if (!normalized || !isValidRussianPlate(normalized)) return
    void validatePlateAvailability()
  }, [plate, validatePlateAvailability])

  const validatePromo = useCallback(async () => {
    const revision = ++promoValidationRevisionRef.current
    const plateSnapshot = normalizePlateInput(plate)
    const promoSnapshot = promoCode
    const affiliateSnapshot = affiliateLink
    validatedPromoSnapshotRef.current = null
    setPromoValid(false)
    setBonusYears(0)
    clearPromoOffer()
    if (promoSnapshot.length < PROMO_MIN_LENGTH) return
    try {
      const result = await garageApi.checkActivationPromo(promoSnapshot, affiliateSnapshot)
      if (
        revision !== promoValidationRevisionRef.current ||
        normalizePlateInput(plate) !== plateSnapshot ||
        promoCode !== promoSnapshot ||
        affiliateSnapshot !== latestAffiliateLinkRef.current
      ) return
      const offer = result as typeof result & { amount_stars?: number; duration_label?: string | null }
      validatedPromoSnapshotRef.current = {
        plate: plateSnapshot,
        promo: promoSnapshot,
        affiliate: affiliateSnapshot,
      }
      setPromoValid(result.ok)
      setPromoError("")
      setBonusYears(result.bonus_years)
      setPromoAmountStars(typeof offer.amount_stars === "number" ? offer.amount_stars : null)
      setPromoDurationLabel(offer.duration_label || null)
      setError("")
    } catch {
      if (
        revision !== promoValidationRevisionRef.current ||
        normalizePlateInput(plate) !== plateSnapshot ||
        promoCode !== promoSnapshot ||
        affiliateSnapshot !== latestAffiliateLinkRef.current
      ) return
      validatedPromoSnapshotRef.current = null
      setPromoError(t("garageActivation.promoInvalid"))
      setChecked(false)
      setError("")
      clearPromoOffer()
    }
  }, [plate, promoCode, affiliateLink, t])

  useEffect(() => {
    if (!promoCode) {
      setPromoValid(false)
      setPromoError("")
      setBonusYears(0)
      clearPromoOffer()
      return
    }
    if (promoCode.length < PROMO_MIN_LENGTH) return
    void validatePromo()
  }, [promoCode, validatePromo])

  const activate = useCallback(async (): Promise<Garage | null> => {
    const normalizedPlate = validatePlate()
    if (!normalizedPlate || busy || !checked) return null
    const promoSnapshot = validatedPromoSnapshotRef.current
    if (
      promoCode &&
      (
        !promoValid ||
        !promoSnapshot ||
        promoSnapshot.plate !== normalizedPlate ||
        promoSnapshot.promo !== promoCode ||
        promoSnapshot.affiliate !== affiliateLink
      )
    ) {
      setPromoError(t("garageActivation.promoInvalid"))
      setChecked(false)
      setError("")
      return null
    }
    setBusy(true)
    setError("")
    try {
      const invoice = await garageApi.createActivationInvoice(
        normalizedPlate,
        promoCode || undefined,
        affiliateLink || undefined,
      )
      const paid = await completeStarsInvoice(invoice)
      if (!paid) {
        setChecked(false)
        return null
      }
      const ownGarage = await garageApi.getMine()
      if (!ownGarage.is_active || ownGarage.vehicle_id == null) {
        throw new Error(t("garageActivation.activateError"))
      }
      return { ...ownGarage, vehicle_id: ownGarage.vehicle_id } as Garage
    } catch (e: any) {
      const message = e?.message || t("garageActivation.activateError")
      if (e instanceof ApiError && e.status === 409) {
        const ownGarage = await garageApi.getMine().catch(() => undefined)
        if (ownGarage && !ownGarage.is_active) {
          const availability = await garageApi.checkActivationPlate(normalizedPlate).catch(() => undefined)
          if (availability && !availability.can_activate) {
            setPlateError(availability.reason || t("garageActivation.plateOccupied"))
            setError("")
            setChecked(false)
            return null
          }
        }
      }
      setError(message)
      setChecked(false)
      return null
    } finally {
      setBusy(false)
    }
  }, [validatePlate, busy, checked, promoCode, promoValid, affiliateLink, t])

  const reset = () => {
    promoValidationRevisionRef.current += 1
    plateValidationRevisionRef.current += 1
    validatedPromoSnapshotRef.current = null
    setPlateState(applyRussianPlateInputMask(initialPlate))
    setPromoState(normalizePromoInput(initialPromo))
    setChecked(false)
    setError("")
    setPlateError("")
    setPromoValid(false)
    setPromoError("")
    setBonusYears(0)
    clearPromoOffer()
  }

  return {
    plate, promoCode, plateError, error, promoValid, promoError, bonusYears,
    promoAmountStars, promoDurationLabel,
    checked, busy, setPlate, setPromoCode, setChecked,
    validatePlateAvailability, validatePromo, activate, reset,
  }
}
